/**
 * DSLExecutionService
 *
 * This service is planned to replace the old CodeExecutionService.
 * It executes an input DSL command and performs geometry operations.
 * Currently supports the "CREATE BOX", "CREATE ARC", "CREATE CIRCLE", "CREATE LINE",
 * "CREATE POLYGON", "CREATE RECTANGLE", "CREATE FOLDER", and now "CREATE FUSE" DSL commands.
 *
 * Expected DSL command formats:
 *   CREATE BOX ORIGIN x y z SIZE dx dy HEIGHT dz
 *   CREATE ARC CENTER x y z START x y z NORMAL x y z ANGLE a
 *   CREATE CIRCLE CENTER x y z RADIUS r NORMAL x y z
 *   CREATE LINE FROM x y z TO x y z
 *   CREATE POLYGON POINTS x1 y1 z1 x2 y2 z2 ... xn yn zn
 *   CREATE RECTANGLE ORIGIN x y z SIZE dx dy
 *   CREATE FOLDER [NAME folderName]
 *   CREATE FUSE BOTTOM <bottom-dsl-command> TOP <top-dsl-command>
 *
 * Examples:
 *   "CREATE BOX ORIGIN 0 0 0 SIZE 100 50 HEIGHT 75"
 *   "CREATE ARC CENTER 0 0 0 START 100 0 0 NORMAL 0 0 1 ANGLE 90"
 *   "CREATE CIRCLE CENTER 0 0 0 RADIUS 50 NORMAL 0 0 1"
 *   "CREATE LINE FROM 0 0 0 TO 100 0 0"
 *   "CREATE POLYGON POINTS 0 0 0 100 0 0 100 100 0 0 100 0"
 *   "CREATE RECTANGLE ORIGIN 0 0 0 SIZE 100 50"
 *   "CREATE FOLDER" or "CREATE FOLDER NAME My Custom Folder"
 *   "CREATE FUSE BOTTOM CREATE BOX ORIGIN 0 0 0 SIZE 100 50 HEIGHT 75 TOP CREATE BOX ORIGIN 50 50 0 SIZE 100 50 HEIGHT 75"
 *
 * When executed, the service parses the parameters, creates the corresponding
 * geometry node, adds it to the active document, and returns the new node.
 */

import {
    I18n,
    IApplication,
    IEdge,
    IService,
    IShape,
    Plane,
    PubSub,
    Result,
    Transaction,
    XYZ,
} from "luminicad-core"; // Geometric types needed for shape creation, added Transaction and PubSub for delete operations

// Import node classes that encapsulate the geometry creation logic and document integration.
import { EditableShapeNode, FolderNode, Ray } from "luminicad-core"; // Import Ray for creating the axis
import { ArcNode } from "../../../luminicad/src/bodys/arc";
import { BooleanNode } from "../../../luminicad/src/bodys/boolean";
import { BoxNode } from "../../../luminicad/src/bodys/box";
import { CircleNode } from "../../../luminicad/src/bodys/circle";
import { FaceNode } from "../../../luminicad/src/bodys/face"; // New import for face node support
import { FuseNode } from "../../../luminicad/src/bodys/fuse";
import { LineNode } from "../../../luminicad/src/bodys/line";
import { PolygonNode } from "../../../luminicad/src/bodys/polygon";
import { PrismNode } from "../../../luminicad/src/bodys/prism"; // New import for prism node
import { RectNode } from "../../../luminicad/src/bodys/rect";
import { RevolvedNode } from "../../../luminicad/src/bodys/revolve"; // New import for revolve node
import { SweepedNode } from "../../../luminicad/src/bodys/sweep"; // New import for sweep node support
import { WireNode } from "../../../luminicad/src/bodys/wire"; // New import for wire node support

// Add new imports for material support
import { MaterialLibrary } from "luminicad-core";

export class DSLExecutionService implements IService {
    private app!: IApplication;
    private variableCommands: Map<string, string> = new Map();
    // New property: store variable node results for selection purpose.
    private variableResults: Map<string, any> = new Map();
    // New property: store the last created node for easy selection default.
    private lastNodeResult: any = null;
    // Counter used to auto-generate folder names when not provided.
    private folderCounter: number = 1;

    constructor() {}

    // Registers the service with the application so that we have access to the active document.
    register(app: IApplication): void {
        this.app = app;
    }

    // Called when the service starts up.
    start(): void {
        console.info(`${DSLExecutionService.name} started`);
    }

    // Called when the service stops.
    stop(): void {
        // Currently nothing to dispose.
    }

    /**
     * Executes one or multiple DSL commands.
     * Supports both single commands and multi-line command sequences.
     *
     * @param dsl The DSL command string or sequence of commands.
     * @returns A promise that resolves to a Result containing the last created node.
     */
    async executeDsl(dsl: string): Promise<Result<any>> {
        try {
            // Get the active document from the application.
            const doc = this.app.activeView?.document;
            if (!doc) {
                return Result.err("No active document");
            }

            // Split the input into individual commands
            const commands = dsl
                .split("\n")
                .map((line) => line.trim())
                // Filter out empty lines and comments
                .filter((line) => line && !line.startsWith("//"));

            let lastResult: Result<any> = Result.err("No valid commands found");

            // Execute each command sequentially
            for (const command of commands) {
                const result = await this.executeCommand(command.trim());
                if (!result.isOk) {
                    return result; // Return on first error
                }
                lastResult = result;
            }

            return lastResult;
        } catch (error) {
            console.error("Error executing DSL:", error);
            return Result.err(`Error executing DSL: ${error}`);
        }
    }

    /**
     * Executes a single DSL command.
     * This contains the original command parsing and execution logic.
     * Now supports material assignment using WITH MATERIAL syntax.
     *
     * @param dsl A single DSL command string.
     * @returns A promise that resolves to a Result containing the new node.
     */
    private async executeCommand(dsl: string): Promise<Result<any>> {
        try {
            const doc = this.app.activeView?.document;
            if (!doc) {
                return Result.err("No active document");
            }

            const trimmedDsl = dsl.trim();

            // Check for variable assignment
            const assignmentMatch = trimmedDsl.match(/^([a-zA-Z][a-zA-Z0-9_]*)\s*=\s*(.+)$/);
            if (assignmentMatch) {
                const [_, variableName, command] = assignmentMatch;

                // Execute the command after the '='
                const result = await this.executeCommand(command);
                if (!result.isOk) {
                    return result;
                }

                // Store the original DSL command text in our variableCommands map.
                this.variableCommands.set(variableName, command);
                // NEW: Store the node result so that it can be referenced later
                this.variableResults.set(variableName, result.value);
                // Also update the last created node reference.
                this.lastNodeResult = result.value;
                return result;
            }

            // Check for variable reference in commands
            if (trimmedDsl.includes("$")) {
                const resolvedDslResult = this.resolveVariables(trimmedDsl);
                if (!resolvedDslResult.isOk) {
                    return Result.err(resolvedDslResult.error);
                }
                return this.executeCommand(resolvedDslResult.value);
            }

            // Check for material specification
            // Format: <shape-command> WITH MATERIAL <material-reference>
            // Example: "CREATE BOX ORIGIN 0 0 0 SIZE 10 10 HEIGHT 10 WITH MATERIAL METALS.POLISHED_STEEL"
            const materialMatch = trimmedDsl.match(/^(.+?)\s+WITH\s+MATERIAL\s+([A-Z]+)\.([A-Z_]+)$/i);

            if (materialMatch) {
                // Extract the shape command and material reference
                const [_, shapeCommand, category, materialName] = materialMatch;

                // Execute the shape creation command first
                const shapeResult = await this.executeCommand(shapeCommand);
                if (!shapeResult.isOk) {
                    return shapeResult;
                }

                // Get material preset based on category
                let materialPresets;
                switch (category.toUpperCase()) {
                    case "METALS":
                        materialPresets = MaterialLibrary.METALS;
                        break;
                    case "PLASTICS":
                        materialPresets = MaterialLibrary.PLASTICS;
                        break;
                    case "WOOD":
                        materialPresets = MaterialLibrary.WOOD;
                        break;
                    case "GLASS":
                        materialPresets = MaterialLibrary.GLASS;
                        break;
                    default:
                        return Result.err(
                            `Unknown material category: ${category}. Available categories: METALS, PLASTICS, WOOD, GLASS`,
                        );
                }

                // Find the material preset (case-insensitive)
                const materialKey = Object.keys(materialPresets).find(
                    (key) => key.toUpperCase() === materialName.toUpperCase(),
                );

                if (!materialKey) {
                    const availableMaterials = Object.keys(materialPresets).join(", ");
                    return Result.err(
                        `Unknown material: ${materialName} in category ${category}. Available materials: ${availableMaterials}`,
                    );
                }

                const materialPreset = materialPresets[materialKey as keyof typeof materialPresets];

                // Create and add the material to the document
                const material = MaterialLibrary.createMaterial(doc, materialPreset);
                doc.materials.push(material); // Using push instead of add for ObservableCollection

                // Set the material ID on the node
                shapeResult.value.materialId = material.id;

                // Force a visual update
                doc.visual.update();

                return shapeResult;
            }

            if (trimmedDsl.toUpperCase().startsWith("CREATE BOX")) {
                // Parse DSL command for a box.
                const parseResult = this.parseBoxCommand(trimmedDsl);
                if (!parseResult.isOk) {
                    return Result.err(parseResult.error);
                }

                // Destructure the box parameters from the parser.
                const { origin, dx, dy, dz } = parseResult.value;

                // Create a default plane using the provided origin.
                // Assume the default normal is [0, 0, 1] (i.e., the XY workplane).
                const plane = this.createDefaultPlane(origin);

                // Create a new BoxNode using the parsed parameters.
                const node = new BoxNode(doc, plane, dx, dy, dz);

                // Add the new node to the document.
                doc.addNode(node);

                // Return the created node.
                this.lastNodeResult = node;
                return Result.ok(node);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE ARC")) {
                // Parse DSL command for an arc.
                const parseResult = this.parseArcCommand(trimmedDsl);
                if (!parseResult.isOk) {
                    return Result.err(parseResult.error);
                }

                // Destructure arc parameters from the parser.
                const { center, start, normal, angle } = parseResult.value;

                // Create a new ArcNode using the parsed parameters.
                // Note: The ArcNode constructor expects (document, normal, center, start, angle).
                const node = new ArcNode(doc, normal, center, start, angle);

                // Add the created arc node to the document.
                doc.addNode(node);

                // Return the created node.
                this.lastNodeResult = node;
                return Result.ok(node);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE CIRCLE")) {
                // Parse DSL command for a circle.
                const parseResult = this.parseCircleCommand(trimmedDsl);
                if (!parseResult.isOk) {
                    return Result.err(parseResult.error);
                }

                // Destructure circle parameters from the parser.
                const { center, radius, normal } = parseResult.value;

                // Create a new CircleNode using the parsed parameters.
                // Note: The CircleNode constructor expects (document, normal, center, radius).
                const node = new CircleNode(doc, normal, center, radius);

                // Add the created circle node to the document.
                doc.addNode(node);

                // Return the created node.
                this.lastNodeResult = node;
                return Result.ok(node);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE LINE")) {
                // Parse DSL command for a line.
                const parseResult = this.parseLineCommand(trimmedDsl);
                if (!parseResult.isOk) {
                    return Result.err(parseResult.error);
                }

                // Destructure line parameters from the parser.
                const { from, to } = parseResult.value;

                // Create a new LineNode using the parsed parameters.
                const node = new LineNode(doc, from, to);

                // Add the created line node to the document.
                doc.addNode(node);

                // Return the created node.
                this.lastNodeResult = node;
                return Result.ok(node);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE POLYGON")) {
                // Parse DSL command for a polygon.
                const parseResult = this.parsePolygonCommand(trimmedDsl);
                if (!parseResult.isOk) {
                    return Result.err(parseResult.error);
                }

                // Destructure polygon points from the parser.
                const { points } = parseResult.value;

                // Create a new PolygonNode using the parsed points.
                const node = new PolygonNode(doc, points);

                // Add the created polygon node to the document.
                doc.addNode(node);

                // Return the created node.
                this.lastNodeResult = node;
                return Result.ok(node);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE RECTANGLE")) {
                // New branch to parse DSL command for a rectangle.
                const parseResult = this.parseRectangleCommand(trimmedDsl);
                if (!parseResult.isOk) {
                    return Result.err(parseResult.error);
                }

                // Destructure rectangle parameters from the parser.
                const { origin, dx, dy } = parseResult.value;

                // Create a default plane using the provided origin.
                const plane = this.createDefaultPlane(origin);

                // Create a new RectNode using the parsed parameters.
                const node = new RectNode(doc, plane, dx, dy);

                // Add the created rectangle node to the document.
                doc.addNode(node);

                // Return the created node.
                this.lastNodeResult = node;
                return Result.ok(node);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE FUSE")) {
                // Expected DSL format:
                // CREATE FUSE BOTTOM <bottom-dsl-command> TOP <top-dsl-command>
                const tokens = trimmedDsl.split(/\s+/);
                if (tokens.length < 6) {
                    return Result.err(
                        "Invalid DSL format for FUSE. Expected format: CREATE FUSE BOTTOM <bottom-dsl-command> TOP <top-dsl-command>",
                    );
                }
                if (
                    tokens[0].toUpperCase() !== "CREATE" ||
                    tokens[1].toUpperCase() !== "FUSE" ||
                    tokens[2].toUpperCase() !== "BOTTOM"
                ) {
                    return Result.err("Invalid DSL FUSE command structure. Please check the keywords.");
                }
                // Find the TOP keyword to separate bottom and top commands
                const topIndex = tokens.findIndex((token, idx) => idx > 2 && token.toUpperCase() === "TOP");
                if (topIndex === -1) {
                    return Result.err("Invalid DSL FUSE command: missing TOP keyword");
                }
                // Extract the bottom and top DSL commands
                const bottomDsl = tokens.slice(3, topIndex).join(" ");
                const topDsl = tokens.slice(topIndex + 1).join(" ");

                // Execute bottom DSL command
                const bottomResult = await this.executeDsl(bottomDsl);
                if (!bottomResult.isOk) {
                    return Result.err(`Error in bottom shape DSL: ${bottomResult.error}`);
                }
                const bottomShapeResult = bottomResult.value.generateShape();
                if (!bottomShapeResult.isOk) {
                    return Result.err(`Bottom shape generation failed: ${bottomShapeResult.error}`);
                }

                // Execute top DSL command
                const topResult = await this.executeDsl(topDsl);
                if (!topResult.isOk) {
                    return Result.err(`Error in top shape DSL: ${topResult.error}`);
                }
                const topShapeResult = topResult.value.generateShape();
                if (!topShapeResult.isOk) {
                    return Result.err(`Top shape generation failed: ${topShapeResult.error}`);
                }

                // Create and return the FuseNode
                const fuseNode = new FuseNode(doc, bottomShapeResult.value, topShapeResult.value);
                doc.addNode(fuseNode);
                this.lastNodeResult = fuseNode;
                return Result.ok(fuseNode);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE FOLDER")) {
                // New branch for folder creation.
                const parseResult = this.parseFolderCommand(trimmedDsl);
                if (!parseResult.isOk) {
                    return Result.err(parseResult.error);
                }
                const { name } = parseResult.value;
                const folder = new FolderNode(doc, name);
                doc.addNode(folder);
                this.lastNodeResult = folder;
                return Result.ok(folder);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE PRISM")) {
                // Handle the CREATE PRISM DSL command.
                // Expected syntax:
                //   CREATE PRISM SECTION <section-dsl-command> LENGTH <length>
                // where the <section-dsl-command> should generate a 2D shape (section) and <length> is the extrusion length.

                const tokens = trimmedDsl.split(/\s+/);
                if (tokens.length < 6) {
                    return Result.err(
                        "Invalid DSL format for PRISM. Expected format: CREATE PRISM SECTION <section-dsl-command> LENGTH <length>",
                    );
                }
                if (
                    tokens[0].toUpperCase() !== "CREATE" ||
                    tokens[1].toUpperCase() !== "PRISM" ||
                    tokens[2].toUpperCase() !== "SECTION"
                ) {
                    return Result.err(
                        "Invalid DSL PRISM command structure. Ensure proper keywords: CREATE PRISM SECTION ...",
                    );
                }
                // Find the index of the LENGTH keyword.
                const lengthIdx = tokens.findIndex(
                    (token, idx) => idx > 2 && token.toUpperCase() === "LENGTH",
                );
                if (lengthIdx === -1 || lengthIdx === tokens.length - 1) {
                    return Result.err("Invalid DSL PRISM command. Missing LENGTH keyword or length value.");
                }
                // Extract the section DSL command by joining tokens between "SECTION" and "LENGTH".
                const sectionDsl = tokens.slice(3, lengthIdx).join(" ");
                // Parse the length value from the token following "LENGTH".
                const parseLength = parseFloat(tokens[lengthIdx + 1]);
                if (isNaN(parseLength)) {
                    return Result.err("Invalid length value in PRISM command. Expected a number.");
                }
                // Recursively execute the section DSL command to generate the section node.
                const sectionResult = await this.executeDsl(sectionDsl);
                if (!sectionResult.isOk) {
                    return Result.err(`Error in section DSL command: ${sectionResult.error}`);
                }
                // Retrieve the generated section shape using the node's generateShape() method.
                const sectionShapeResult = sectionResult.value.generateShape();
                if (!sectionShapeResult.isOk) {
                    return Result.err(`Section shape generation failed: ${sectionShapeResult.error}`);
                }
                // Create a new PrismNode using the generated section shape and the parsed extrusion length.
                const prismNode = new PrismNode(doc, sectionShapeResult.value, parseLength);
                // Add the prism node to the document.
                doc.addNode(prismNode);
                // Return the created prism node.
                this.lastNodeResult = prismNode;
                return Result.ok(prismNode);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE REVOLVE")) {
                // Expected DSL format:
                // CREATE REVOLVE PROFILE <profile-dsl-command> AXIS ORIGIN x y z DIRECTION dx dy dz ANGLE a

                // Split tokens for processing.
                const tokens = trimmedDsl.split(/\s+/);
                // Find the index of the 'AXIS' keyword.
                const axisIndex = tokens.findIndex(
                    (token, idx) => idx > 2 && token.toUpperCase() === "AXIS",
                );
                if (axisIndex === -1) {
                    return Result.err("Invalid DSL REVOLVE command. Missing AXIS keyword.");
                }
                // Extract the profile DSL command between "PROFILE" and "AXIS".
                const profileDsl = tokens.slice(3, axisIndex).join(" ");

                // Verify minimum tokens after AXIS for axis data.
                if (tokens.length < axisIndex + 11) {
                    return Result.err("Invalid DSL REVOLVE command. Incomplete axis definition.");
                }
                // Expect "ORIGIN" keyword
                if (tokens[axisIndex + 1].toUpperCase() !== "ORIGIN") {
                    return Result.err("Invalid DSL REVOLVE command. Expected ORIGIN keyword after AXIS.");
                }
                // Parse origin coordinates.
                const ox = parseFloat(tokens[axisIndex + 2]);
                const oy = parseFloat(tokens[axisIndex + 3]);
                const oz = parseFloat(tokens[axisIndex + 4]);
                if (isNaN(ox) || isNaN(oy) || isNaN(oz)) {
                    return Result.err("Invalid origin coordinates in DSL REVOLVE command.");
                }
                // Expect "DIRECTION" keyword.
                if (tokens[axisIndex + 5].toUpperCase() !== "DIRECTION") {
                    return Result.err(
                        "Invalid DSL REVOLVE command. Expected DIRECTION keyword after origin.",
                    );
                }
                // Parse direction vector.
                const dx = parseFloat(tokens[axisIndex + 6]);
                const dy = parseFloat(tokens[axisIndex + 7]);
                const dz = parseFloat(tokens[axisIndex + 8]);
                if (isNaN(dx) || isNaN(dy) || isNaN(dz)) {
                    return Result.err("Invalid direction vector in DSL REVOLVE command.");
                }
                // Expect "ANGLE" keyword.
                if (tokens[axisIndex + 9].toUpperCase() !== "ANGLE") {
                    return Result.err(
                        "Invalid DSL REVOLVE command. Expected ANGLE keyword after direction.",
                    );
                }
                // Parse angle value.
                const angle = parseFloat(tokens[axisIndex + 10]);
                if (isNaN(angle)) {
                    return Result.err("Invalid angle value in DSL REVOLVE command.");
                }

                // Recursively execute the profile DSL command to generate the profile node.
                const profileResult = await this.executeDsl(profileDsl);
                if (!profileResult.isOk) {
                    return Result.err(`Error in profile DSL command: ${profileResult.error}`);
                }
                // Retrieve the generated profile shape using the node's generateShape() method.
                const profileShapeResult = profileResult.value.generateShape();
                if (!profileShapeResult.isOk) {
                    return Result.err(`Profile shape generation failed: ${profileShapeResult.error}`);
                }

                // Create a new Ray using the parsed origin and direction.
                const origin = new XYZ(ox, oy, oz);
                const direction = new XYZ(dx, dy, dz);
                const axisRay = new Ray(origin, direction);

                // Create a new RevolvedNode using the generated profile shape, axis, and angle.
                const revolvedNode = new RevolvedNode(doc, profileShapeResult.value, axisRay, angle);
                doc.addNode(revolvedNode);
                this.lastNodeResult = revolvedNode;
                return Result.ok(revolvedNode);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE SWEEP")) {
                // Expected DSL syntax:
                // CREATE SWEEP PROFILE <profile-dsl-command> PATH <path-dsl-command>
                // This branch handles the sweep command by generating both the profile and the path nodes,
                // ensuring that if the path is not already a wire, it is converted appropriately within the SweepedNode constructor.
                const tokens = trimmedDsl.split(/\s+/);
                // Validate minimal structure for SWEEP command (at least 6 tokens are required).
                if (tokens.length < 6) {
                    return Result.err(
                        "Invalid DSL format for SWEEP. Expected format: CREATE SWEEP PROFILE <profile-dsl-command> PATH <path-dsl-command>",
                    );
                }
                // Ensure the command starts with the required keywords: "CREATE SWEEP PROFILE"
                if (
                    tokens[0].toUpperCase() !== "CREATE" ||
                    tokens[1].toUpperCase() !== "SWEEP" ||
                    tokens[2].toUpperCase() !== "PROFILE"
                ) {
                    return Result.err(
                        "Invalid DSL SWEEP command structure. Ensure the command starts with: CREATE SWEEP PROFILE ...",
                    );
                }
                // Locate the "PATH" keyword to separate the profile and path DSL commands.
                const pathIndex = tokens.findIndex(
                    (token, idx) => idx > 2 && token.toUpperCase() === "PATH",
                );
                if (pathIndex === -1) {
                    return Result.err("Invalid DSL SWEEP command: missing PATH keyword");
                }
                // Extract the profile DSL command (tokens between "PROFILE" and "PATH").
                const profileDsl = tokens.slice(3, pathIndex).join(" ");
                // Extract the path DSL command (tokens after "PATH").
                const pathDsl = tokens.slice(pathIndex + 1).join(" ");

                // Recursively execute the profile DSL command to generate the profile node.
                const profileResult = await this.executeDsl(profileDsl);
                if (!profileResult.isOk) {
                    return Result.err(`Error in profile DSL command: ${profileResult.error}`);
                }
                // Retrieve the generated profile shape by calling generateShape().
                const profileShapeResult = profileResult.value.generateShape();
                if (!profileShapeResult.isOk) {
                    return Result.err(`Profile shape generation failed: ${profileShapeResult.error}`);
                }

                // Recursively execute the path DSL command to generate the path node.
                const pathResult = await this.executeDsl(pathDsl);
                if (!pathResult.isOk) {
                    return Result.err(`Error in path DSL command: ${pathResult.error}`);
                }
                // Retrieve the generated path shape by calling generateShape().
                const pathShapeResult = pathResult.value.generateShape();
                if (!pathShapeResult.isOk) {
                    return Result.err(`Path shape generation failed: ${pathShapeResult.error}`);
                }

                // Create a new SweepedNode using the generated profile and path shapes.
                // The SweepedNode implementation (in packages/luminicad/src/bodys/sweep.ts) will ensure that
                // if the path shape is not a wire, it is converted using the shapeFactory.
                const sweepedNode = new SweepedNode(doc, profileShapeResult.value, pathShapeResult.value);
                // Add the created node to the document.
                doc.addNode(sweepedNode);
                // Return the created sweep node.
                this.lastNodeResult = sweepedNode;
                return Result.ok(sweepedNode);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE WIRE")) {
                // Expected DSL format:
                // CREATE WIRE EDGES <edge-dsl-command> [AND <edge-dsl-command> ...]
                // This command combines multiple edge-generating DSL sub-commands into a single wire.

                // Split the DSL command into tokens to verify the presence of the "EDGES" keyword.
                const tokens = trimmedDsl.split(/\s+/);
                if (tokens.length < 4 || tokens[2].toUpperCase() !== "EDGES") {
                    return Result.err(
                        "Invalid DSL format for WIRE. Expected format: CREATE WIRE EDGES <edge-dsl-command> [AND <edge-dsl-command> ...]",
                    );
                }

                // Extract the substring after the fixed "CREATE WIRE EDGES" portion.
                // Note: This extraction assumes the DSL command is written as expected.
                const edgesCommandStr = trimmedDsl.substring("CREATE WIRE EDGES".length).trim();
                if (!edgesCommandStr) {
                    return Result.err("No edge DSL command provided for WIRE.");
                }

                // Split the DSL string into sub-commands using 'AND' as a delimiter (case-insensitive).
                const edgeCommands = edgesCommandStr.split(/\s+AND\s+/i);
                const edges: IEdge[] = []; // Updated: Explicitly type the edges array as IEdge[] to match the WireNode's expectation.

                // Iterate over each edge DSL command.
                for (const cmd of edgeCommands) {
                    // Recursively execute each edge DSL command.
                    const edgeResult = await this.executeDsl(cmd.trim());
                    if (!edgeResult.isOk) {
                        return Result.err(`Error in edge DSL command: ${edgeResult.error}`);
                    }

                    // Retrieve the generated edge shape using the node's generateShape() method.
                    const edgeShapeResult = edgeResult.value.generateShape();
                    if (!edgeShapeResult.isOk) {
                        return Result.err(`Edge shape generation failed: ${edgeShapeResult.error}`);
                    }

                    // Push the generated shape (expected to be an IEdge) into the edges array.
                    edges.push(edgeShapeResult.value);
                }

                // Create a new WireNode with the accumulated edges.
                const wireNode = new WireNode(doc, edges);
                // Add the new WireNode into the active document.
                doc.addNode(wireNode);
                this.lastNodeResult = wireNode;
                return Result.ok(wireNode);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE BOOLEAN")) {
                const booleanRegex = /^CREATE BOOLEAN\s+(COMMON|CUT|FUSE)\s+FIRST\s+(.+?)\s+SECOND\s+(.+)$/i;
                const match = trimmedDsl.match(booleanRegex);
                if (!match) {
                    return Result.err(
                        "Invalid DSL BOOLEAN command format. Expected: CREATE BOOLEAN <OPERATION> FIRST <first-shape> SECOND <second-shape>",
                    );
                }

                const operation = match[1].toUpperCase();
                let firstShapeDsl = match[2].trim();
                let secondShapeDsl = match[3].trim();

                // Handle variable references for first shape
                if (firstShapeDsl.startsWith("$")) {
                    const varName = firstShapeDsl.substring(1);
                    const varValue = this.variableCommands.get(varName);
                    if (!varValue) {
                        return Result.err(`Variable '${varName}' not found`);
                    }
                    firstShapeDsl = varValue;
                }

                // Handle variable references for second shape
                if (secondShapeDsl.startsWith("$")) {
                    const varName = secondShapeDsl.substring(1);
                    const varValue = this.variableCommands.get(varName);
                    if (!varValue) {
                        return Result.err(`Variable '${varName}' not found`);
                    }
                    secondShapeDsl = varValue;
                }

                // Execute first shape DSL command
                const firstResult = await this.executeDsl(firstShapeDsl);
                if (!firstResult.isOk) {
                    return Result.err(`Error in first shape DSL command: ${firstResult.error}`);
                }
                const firstNode = firstResult.value;
                const firstShapeResult = firstNode.generateShape();
                if (!firstShapeResult.isOk) {
                    return Result.err(`First shape generation failed: ${firstShapeResult.error}`);
                }

                // Execute second shape DSL command
                const secondResult = await this.executeDsl(secondShapeDsl);
                if (!secondResult.isOk) {
                    return Result.err(`Error in second shape DSL command: ${secondResult.error}`);
                }
                const secondNode = secondResult.value;
                const secondShapeResult = secondNode.generateShape();
                if (!secondShapeResult.isOk) {
                    return Result.err(`Second shape generation failed: ${secondShapeResult.error}`);
                }

                // Perform the boolean operation
                let booleanResult: Result<IShape>;
                switch (operation) {
                    case "COMMON":
                        booleanResult = this.app.shapeFactory.booleanCommon(
                            firstShapeResult.value,
                            secondShapeResult.value,
                        );
                        break;
                    case "CUT":
                        booleanResult = this.app.shapeFactory.booleanCut(
                            firstShapeResult.value,
                            secondShapeResult.value,
                        );
                        break;
                    case "FUSE":
                        booleanResult = this.app.shapeFactory.booleanFuse(
                            firstShapeResult.value,
                            secondShapeResult.value,
                        );
                        break;
                    default:
                        return Result.err("Unexpected boolean operation type");
                }

                if (!booleanResult.isOk) {
                    return Result.err(`Boolean operation failed: ${booleanResult.error}`);
                }

                // Create the boolean node
                const booleanNode = new BooleanNode(doc, booleanResult.value);
                doc.addNode(booleanNode);

                // Remove the original nodes after successful boolean operation
                firstNode.parent?.remove(firstNode);
                secondNode.parent?.remove(secondNode);

                this.lastNodeResult = booleanNode;
                return Result.ok(booleanNode);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE BEZIER")) {
                // Expected format: CREATE BEZIER POINTS x1 y1 z1 x2 y2 z2 ... xn yn zn
                // At least 2 points are required to create a Bezier curve
                const tokens = trimmedDsl.split(/\s+/);

                if (tokens.length < 8) {
                    // CREATE BEZIER POINTS + at least 2 points (6 coordinates)
                    return Result.err(
                        "Invalid DSL format for BEZIER. Expected format: CREATE BEZIER POINTS x1 y1 z1 x2 y2 z2 ... xn yn zn",
                    );
                }

                if (
                    tokens[0].toUpperCase() !== "CREATE" ||
                    tokens[1].toUpperCase() !== "BEZIER" ||
                    tokens[2].toUpperCase() !== "POINTS"
                ) {
                    return Result.err("Invalid DSL BEZIER command structure. Please check the keywords.");
                }

                // Parse all points from the remaining tokens
                const coordinates = tokens.slice(3).map(Number);
                if (coordinates.some(isNaN)) {
                    return Result.err("Invalid coordinates in BEZIER command. All values must be numbers.");
                }

                if (coordinates.length % 3 !== 0) {
                    return Result.err(
                        "Invalid number of coordinates. Each point must have x, y, and z values.",
                    );
                }

                // Create array of XYZ points
                const points: XYZ[] = [];
                for (let i = 0; i < coordinates.length; i += 3) {
                    points.push(new XYZ(coordinates[i], coordinates[i + 1], coordinates[i + 2]));
                }

                if (points.length < 2) {
                    return Result.err("At least 2 points are required to create a Bezier curve.");
                }

                // Create the Bezier curve using the shape factory
                const bezierResult = this.app.shapeFactory.bezier(points);
                if (!bezierResult.isOk) {
                    return Result.err(`Failed to create Bezier curve: ${bezierResult.error}`);
                }

                // Create an EditableShapeNode with the Bezier curve
                const node = new EditableShapeNode(
                    doc,
                    I18n.translate("command.bezier"),
                    bezierResult.value,
                );

                doc.addNode(node);
                this.lastNodeResult = node;
                return Result.ok(node);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE FACE")) {
                // Expected DSL format:
                // CREATE FACE WIRE <wire-dsl-command>
                // or
                // CREATE FACE EDGES <edge-dsl-command> [AND <edge-dsl-command> ...]

                const tokens = trimmedDsl.split(/\s+/);
                if (tokens.length < 4) {
                    return Result.err(
                        "Invalid DSL format for FACE. Expected format: CREATE FACE WIRE <wire-dsl> or CREATE FACE EDGES <edge-dsl> [AND <edge-dsl> ...]",
                    );
                }

                if (tokens[2].toUpperCase() === "WIRE") {
                    // Handle single wire case
                    const wireDsl = tokens.slice(3).join(" ");
                    const wireResult = await this.executeDsl(wireDsl);
                    if (!wireResult.isOk) {
                        return Result.err(`Error in wire DSL command: ${wireResult.error}`);
                    }
                    const wireShapeResult = wireResult.value.generateShape();
                    if (!wireShapeResult.isOk) {
                        return Result.err(`Wire shape generation failed: ${wireShapeResult.error}`);
                    }

                    // Create face from wire
                    const faceNode = new FaceNode(doc, [wireShapeResult.value]);
                    doc.addNode(faceNode);

                    // Remove the original wire node
                    wireResult.value.parent?.remove(wireResult.value);

                    this.lastNodeResult = faceNode;
                    return Result.ok(faceNode);
                } else if (tokens[2].toUpperCase() === "EDGES") {
                    // Extract the substring after "EDGES"
                    const edgesCommandStr = trimmedDsl.substring("CREATE FACE EDGES".length).trim();
                    if (!edgesCommandStr) {
                        return Result.err("No edge DSL commands provided for FACE.");
                    }

                    // Split the DSL string into sub-commands using 'AND' as a delimiter
                    const edgeCommands = edgesCommandStr.split(/\s+AND\s+/i);
                    const edges: IEdge[] = [];

                    // Execute each edge DSL command
                    for (const cmd of edgeCommands) {
                        const edgeResult = await this.executeDsl(cmd.trim());
                        if (!edgeResult.isOk) {
                            return Result.err(`Error in edge DSL command: ${edgeResult.error}`);
                        }

                        const edgeShapeResult = edgeResult.value.generateShape();
                        if (!edgeShapeResult.isOk) {
                            return Result.err(`Edge shape generation failed: ${edgeShapeResult.error}`);
                        }

                        edges.push(edgeShapeResult.value as IEdge);

                        // Remove the original edge node
                        edgeResult.value.parent?.remove(edgeResult.value);
                    }

                    // Create face from edges
                    const faceNode = new FaceNode(doc, edges);
                    doc.addNode(faceNode);
                    this.lastNodeResult = faceNode;
                    return Result.ok(faceNode);
                } else {
                    return Result.err(
                        "Invalid DSL FACE command. Expected 'WIRE' or 'EDGES' keyword after 'CREATE FACE'",
                    );
                }
            } else if (trimmedDsl.toUpperCase().startsWith("DELETE")) {
                // This branch handles the DELETE DSL command.
                // When a user enters a DSL command that starts with "DELETE", this command
                // will remove all selected nodes from the active document.
                // The deletion logic is wrapped in a transaction to ensure that the deletion
                // is performed atomically, similar to the command defined in packages/luminicad/src/commands/delete.ts

                // Retrieve the active document again.
                const doc = this.app.activeView?.document;
                if (!doc) {
                    return Result.err("No active document");
                }

                // Retrieve and store the currently selected nodes before deletion.
                const nodesToDelete = doc.selection.getSelectedNodes();
                const count = nodesToDelete.length;

                // Execute the deletion inside a transaction.
                Transaction.execute(doc, "delete", () => {
                    // If the current active node is among those selected for deletion,
                    // reset the current node to the root node.
                    if (doc.currentNode && nodesToDelete.includes(doc.currentNode)) {
                        doc.currentNode = doc.rootNode;
                    }
                    // Clear the selection so no deleted nodes remain selected.
                    doc.selection.clearSelection();
                    // Iterate over all selected nodes and remove each from its parent.
                    nodesToDelete.forEach((model) => model.parent?.remove(model));
                    // Update the visual representation of the document.
                    doc.visual.update();
                    // Publish a toast notification to inform the user about the deletion.
                    PubSub.default.pub("showToast", "toast.delete{0}Objects", count);
                });

                // Return a successful result containing a message indicating how many nodes were deleted.
                return Result.ok({ message: `Deleted ${count} object${count === 1 ? "" : "s"}` });
            } else if (trimmedDsl.toUpperCase().startsWith("SELECT SHAPE")) {
                const tokens = trimmedDsl.split(/\s+/);
                let targetNode;
                if (tokens.length === 2) {
                    // No variable specified; default to last created node.
                    if (!this.lastNodeResult) {
                        return Result.err("No shape available to select.");
                    }
                    targetNode = this.lastNodeResult;
                } else {
                    // Determine if user specified "VARIABLE" explicitly or just a name.
                    let variableName: string;
                    if (tokens[2].toUpperCase() === "VARIABLE") {
                        if (tokens.length < 4)
                            return Result.err("No variable name provided for SELECT SHAPE.");
                        variableName = tokens[3];
                    } else {
                        variableName = tokens[2];
                    }
                    targetNode = this.variableResults.get(variableName);
                    if (!targetNode) {
                        return Result.err(`No shape stored in variable: ${variableName}`);
                    }
                }
                // Use the document selection service to select the node.
                doc.selection.setSelection([targetNode], false);
                // Force a visual update so the selection is shown.
                doc.visual.update();
                return Result.ok(targetNode);
            } else if (trimmedDsl.toUpperCase().startsWith("CREATE THICKSOLID")) {
                // Expected DSL format:
                //   CREATE THICKSOLID <base-shape-dsl> THICKNESS <thickness>
                // where <base-shape-dsl> is a valid DSL command that produces a shape,
                // and <thickness> is a numeric value representing the thickness.

                // Split the DSL command into tokens.
                const tokens = trimmedDsl.split(/\s+/);
                // Find the index of the "THICKNESS" keyword (case insensitive).
                const thicknessIndex = tokens.findIndex((token) => token.toUpperCase() === "THICKNESS");
                if (thicknessIndex === -1 || thicknessIndex === tokens.length - 1) {
                    // If "THICKNESS" keyword is missing or no value follows, return an error.
                    return Result.err(
                        "Invalid DSL format for THICKSOLID. Expected format: CREATE THICKSOLID <base-shape-dsl> THICKNESS <thickness>",
                    );
                }

                // Extract the base shape DSL command.
                // Skip the first two tokens ("CREATE THICKSOLID") and take the tokens up to "THICKNESS".
                const baseDsl = tokens.slice(2, thicknessIndex).join(" ");
                if (!baseDsl) {
                    return Result.err(
                        "Invalid DSL format for THICKSOLID. Missing base shape command before THICKNESS.",
                    );
                }

                // Parse the thickness value from the token immediately after "THICKNESS".
                const thicknessValue = parseFloat(tokens[thicknessIndex + 1]);
                if (isNaN(thicknessValue)) {
                    return Result.err("Invalid thickness value in THICKSOLID command. Expected a number.");
                }

                // Execute the base shape DSL command recursively.
                const baseResult = await this.executeCommand(baseDsl);
                if (!baseResult.isOk) {
                    // Return an error if the base command fails.
                    return Result.err(`Error in base shape DSL command: ${baseResult.error}`);
                }

                // Retrieve the generated shape from the executed base node.
                const baseShapeResult = baseResult.value.generateShape();
                if (!baseShapeResult.isOk) {
                    return Result.err(`Base shape generation failed: ${baseShapeResult.error}`);
                }

                // Create the thick solid geometry using the shape factory.
                // This uses the makeThickSolidBySimple method, similar to the ThickSolidCommand.
                const thickSolidResult = this.app.shapeFactory.makeThickSolidBySimple(
                    baseShapeResult.value,
                    thicknessValue,
                );
                if (!thickSolidResult.isOk) {
                    return Result.err(`Thick solid generation failed: ${thickSolidResult.error}`);
                }

                // Create a new EditableShapeNode with the generated thick solid geometry.
                // I18n.translate is used for localized naming.
                const thickSolidNode = new EditableShapeNode(
                    doc,
                    I18n.translate("command.thickSolid"),
                    thickSolidResult.value,
                );
                // Add the new node to the document.
                doc.addNode(thickSolidNode);
                // Update the reference to the last created node.
                this.lastNodeResult = thickSolidNode;
                // Return the result containing the thick solid node.
                return Result.ok(thickSolidNode);
            } else {
                return Result.err("Unknown DSL command");
            }
        } catch (error) {
            console.error("Error executing command:", error);
            return Result.err(`Error executing command: ${error}`);
        }
    }

    // -------------------------------------------------------------------
    // Below are the helper parsing functions for various DSL commands.
    // -------------------------------------------------------------------

    /**
     * Parses a "CREATE BOX" DSL command.
     *
     * Expected command format:
     *   CREATE BOX ORIGIN x y z SIZE dx dy HEIGHT dz
     *
     * @param dsl The DSL command string.
     * @returns A Result containing the parsed box parameters, or an error message.
     */
    private parseBoxCommand(dsl: string): Result<{ origin: XYZ; dx: number; dy: number; dz: number }> {
        const tokens = dsl.split(/\s+/);
        if (tokens.length !== 11) {
            return Result.err(
                "Invalid DSL format. Expected format: CREATE BOX ORIGIN x y z SIZE dx dy HEIGHT dz",
            );
        }
        if (
            tokens[0].toUpperCase() !== "CREATE" ||
            tokens[1].toUpperCase() !== "BOX" ||
            tokens[2].toUpperCase() !== "ORIGIN" ||
            tokens[6].toUpperCase() !== "SIZE" ||
            tokens[9].toUpperCase() !== "HEIGHT"
        ) {
            return Result.err("Invalid DSL BOX command structure. Please check the keywords.");
        }
        const parseNumber = (str: string): number | null => {
            const num = parseFloat(str);
            return isNaN(num) ? null : num;
        };
        const x = parseNumber(tokens[3]);
        const y = parseNumber(tokens[4]);
        const z = parseNumber(tokens[5]);
        if (x === null || y === null || z === null) {
            return Result.err("Invalid origin coordinates. Expected numbers for x, y, and z.");
        }
        const dx = parseNumber(tokens[7]);
        const dy = parseNumber(tokens[8]);
        if (dx === null || dy === null) {
            return Result.err("Invalid size parameters. Expected numbers for dx and dy.");
        }
        const dz = parseNumber(tokens[10]);
        if (dz === null) {
            return Result.err("Invalid height parameter. Expected a number for dz.");
        }
        return Result.ok({ origin: new XYZ(x, y, z), dx, dy, dz });
    }

    /**
     * Parses a "CREATE ARC" DSL command.
     *
     * Expected command format:
     *   CREATE ARC CENTER x y z START x y z NORMAL x y z ANGLE a
     *
     * @param dsl The DSL command string.
     * @returns A Result containing the parsed arc parameters, or an error message.
     */
    private parseArcCommand(dsl: string): Result<{ center: XYZ; start: XYZ; normal: XYZ; angle: number }> {
        const tokens = dsl.split(/\s+/);
        if (tokens.length !== 16) {
            return Result.err(
                "Invalid DSL format for ARC. Expected format: CREATE ARC CENTER x y z START x y z NORMAL x y z ANGLE a",
            );
        }
        if (
            tokens[0].toUpperCase() !== "CREATE" ||
            tokens[1].toUpperCase() !== "ARC" ||
            tokens[2].toUpperCase() !== "CENTER" ||
            tokens[6].toUpperCase() !== "START" ||
            tokens[10].toUpperCase() !== "NORMAL" ||
            tokens[14].toUpperCase() !== "ANGLE"
        ) {
            return Result.err("Invalid DSL ARC command structure. Please check the keywords.");
        }
        const parseNumber = (str: string): number | null => {
            const num = parseFloat(str);
            return isNaN(num) ? null : num;
        };
        const cx = parseNumber(tokens[3]);
        const cy = parseNumber(tokens[4]);
        const cz = parseNumber(tokens[5]);
        if (cx === null || cy === null || cz === null) {
            return Result.err("Invalid center coordinates. Expected numbers for x, y, and z.");
        }
        const sx = parseNumber(tokens[7]);
        const sy = parseNumber(tokens[8]);
        const sz = parseNumber(tokens[9]);
        if (sx === null || sy === null || sz === null) {
            return Result.err("Invalid start coordinates. Expected numbers for x, y, and z.");
        }
        const nx = parseNumber(tokens[11]);
        const ny = parseNumber(tokens[12]);
        const nz = parseNumber(tokens[13]);
        if (nx === null || ny === null || nz === null) {
            return Result.err("Invalid normal coordinates. Expected numbers for x, y, and z.");
        }
        const angle = parseNumber(tokens[15]);
        if (angle === null) {
            return Result.err("Invalid angle value. Expected a number for angle.");
        }
        return Result.ok({
            center: new XYZ(cx, cy, cz),
            start: new XYZ(sx, sy, sz),
            normal: new XYZ(nx, ny, nz),
            angle: angle,
        });
    }

    /**
     * Parses a "CREATE CIRCLE" DSL command.
     *
     * Expected command format:
     *   CREATE CIRCLE CENTER x y z RADIUS r NORMAL x y z
     *
     * @param dsl The DSL command string.
     * @returns A Result containing the parsed circle parameters, or an error message.
     */
    private parseCircleCommand(dsl: string): Result<{ center: XYZ; radius: number; normal: XYZ }> {
        const tokens = dsl.split(/\s+/);
        if (tokens.length !== 12) {
            return Result.err(
                "Invalid DSL format. Expected format: CREATE CIRCLE CENTER x y z RADIUS r NORMAL x y z",
            );
        }
        if (
            tokens[0].toUpperCase() !== "CREATE" ||
            tokens[1].toUpperCase() !== "CIRCLE" ||
            tokens[2].toUpperCase() !== "CENTER" ||
            tokens[6].toUpperCase() !== "RADIUS" ||
            tokens[8].toUpperCase() !== "NORMAL"
        ) {
            return Result.err("Invalid DSL CIRCLE command structure. Please check the keywords.");
        }
        const parseNumber = (str: string): number | null => {
            const num = parseFloat(str);
            return isNaN(num) ? null : num;
        };
        const cx = parseNumber(tokens[3]);
        const cy = parseNumber(tokens[4]);
        const cz = parseNumber(tokens[5]);
        if (cx === null || cy === null || cz === null) {
            return Result.err("Invalid center coordinates. Expected numbers for x, y, and z.");
        }
        const radius = parseNumber(tokens[7]);
        if (radius === null) {
            return Result.err("Invalid radius value. Expected a number.");
        }
        const nx = parseNumber(tokens[9]);
        const ny = parseNumber(tokens[10]);
        const nz = parseNumber(tokens[11]);
        if (nx === null || ny === null || nz === null) {
            return Result.err("Invalid normal coordinates. Expected numbers for x, y, and z.");
        }
        return Result.ok({
            center: new XYZ(cx, cy, cz),
            radius: radius,
            normal: new XYZ(nx, ny, nz),
        });
    }

    /**
     * Parses a "CREATE LINE" DSL command.
     *
     * Expected command format:
     *   CREATE LINE FROM x y z TO x y z
     *
     * @param dsl The DSL command string.
     * @returns A Result containing the parsed line parameters, or an error message.
     */
    private parseLineCommand(dsl: string): Result<{ from: XYZ; to: XYZ }> {
        const tokens = dsl.split(/\s+/);
        if (tokens.length !== 10) {
            return Result.err(
                "Invalid DSL format for LINE. Expected format: CREATE LINE FROM x y z TO x y z",
            );
        }
        if (
            tokens[0].toUpperCase() !== "CREATE" ||
            tokens[1].toUpperCase() !== "LINE" ||
            tokens[2].toUpperCase() !== "FROM" ||
            tokens[6].toUpperCase() !== "TO"
        ) {
            return Result.err("Invalid DSL LINE command structure. Please check the keywords.");
        }
        const parseNumber = (str: string): number | null => {
            const num = parseFloat(str);
            return isNaN(num) ? null : num;
        };
        const fx = parseNumber(tokens[3]);
        const fy = parseNumber(tokens[4]);
        const fz = parseNumber(tokens[5]);
        if (fx === null || fy === null || fz === null) {
            return Result.err("Invalid start coordinates. Expected numbers for x, y, and z.");
        }
        const tx = parseNumber(tokens[7]);
        const ty = parseNumber(tokens[8]);
        const tz = parseNumber(tokens[9]);
        if (tx === null || ty === null || tz === null) {
            return Result.err("Invalid end coordinates. Expected numbers for x, y, and z.");
        }
        return Result.ok({ from: new XYZ(fx, fy, fz), to: new XYZ(tx, ty, tz) });
    }

    /**
     * Parses a "CREATE POLYGON" DSL command.
     *
     * Expected command format:
     *   CREATE POLYGON POINTS x1 y1 z1 x2 y2 z2 ... xn yn zn
     *
     * The command must define at least three points (i.e., at least 12 tokens total).
     *
     * @param dsl The DSL command string.
     * @returns A Result containing the parsed polygon points, or an error message.
     */
    private parsePolygonCommand(dsl: string): Result<{ points: XYZ[] }> {
        // Split the DSL text into tokens by whitespace.
        const tokens = dsl.split(/\s+/);
        // Check minimum tokens: "CREATE POLYGON POINTS" (3 tokens) + 3 coordinates per point (min 3 points => 9 tokens) = at least 12 tokens.
        if (tokens.length < 12) {
            return Result.err(
                "Invalid DSL format for POLYGON. Expected format: CREATE POLYGON POINTS x y z [x y z ...]. At least three points are required.",
            );
        }

        // Validate fixed keywords.
        if (
            tokens[0].toUpperCase() !== "CREATE" ||
            tokens[1].toUpperCase() !== "POLYGON" ||
            tokens[2].toUpperCase() !== "POINTS"
        ) {
            return Result.err("Invalid DSL POLYGON command structure. Please check the keywords.");
        }

        // Remove the fixed tokens and parse the remaining coordinate tokens.
        const coordinateTokens = tokens.slice(3);
        if (coordinateTokens.length % 3 !== 0) {
            return Result.err(
                "Invalid DSL format for POLYGON. Coordinates must be in groups of three: x y z.",
            );
        }

        const numPoints = coordinateTokens.length / 3;
        if (numPoints < 3) {
            return Result.err("A polygon must have at least three points.");
        }

        // Helper to parse a single number.
        const parseNumber = (str: string): number | null => {
            const num = parseFloat(str);
            return isNaN(num) ? null : num;
        };

        const points: XYZ[] = [];
        // Iterate over the coordinate tokens in groups of three.
        for (let i = 0; i < coordinateTokens.length; i += 3) {
            const x = parseNumber(coordinateTokens[i]);
            const y = parseNumber(coordinateTokens[i + 1]);
            const z = parseNumber(coordinateTokens[i + 2]);
            if (x === null || y === null || z === null) {
                return Result.err("Invalid polygon point coordinates. Expected numbers for x, y, and z.");
            }
            points.push(new XYZ(x, y, z));
        }
        return Result.ok({ points });
    }

    /**
     * Parses a "CREATE RECTANGLE" DSL command.
     *
     * Expected command format:
     *   CREATE RECTANGLE ORIGIN x y z SIZE dx dy
     *
     * @param dsl The DSL command string.
     * @returns A Result containing the parsed rectangle parameters, or an error message.
     */
    private parseRectangleCommand(dsl: string): Result<{ origin: XYZ; dx: number; dy: number }> {
        const tokens = dsl.split(/\s+/);
        if (tokens.length !== 9) {
            return Result.err(
                "Invalid DSL format for RECTANGLE. Expected format: CREATE RECTANGLE ORIGIN x y z SIZE dx dy",
            );
        }
        if (
            tokens[0].toUpperCase() !== "CREATE" ||
            tokens[1].toUpperCase() !== "RECTANGLE" ||
            tokens[2].toUpperCase() !== "ORIGIN" ||
            tokens[6].toUpperCase() !== "SIZE"
        ) {
            return Result.err("Invalid DSL RECTANGLE command structure. Please check the keywords.");
        }
        const parseNumber = (str: string): number | null => {
            const num = parseFloat(str);
            return isNaN(num) ? null : num;
        };
        const ox = parseNumber(tokens[3]);
        const oy = parseNumber(tokens[4]);
        const oz = parseNumber(tokens[5]);
        if (ox === null || oy === null || oz === null) {
            return Result.err("Invalid origin coordinates. Expected numbers for x, y, and z.");
        }
        const dx = parseNumber(tokens[7]);
        const dy = parseNumber(tokens[8]);
        if (dx === null || dy === null) {
            return Result.err("Invalid size parameters. Expected numbers for dx and dy.");
        }
        return Result.ok({ origin: new XYZ(ox, oy, oz), dx, dy });
    }

    /**
     * Parses a "CREATE FOLDER" DSL command.
     *
     * Expected command formats:
     *   CREATE FOLDER
     *   CREATE FOLDER NAME folderName
     *
     * If no folder name is provided, an auto-generated name is used (e.g., Folder1).
     *
     * @param dsl The DSL command string.
     * @returns A Result containing the folder name, or an error message.
     */
    private parseFolderCommand(dsl: string): Result<{ name: string }> {
        const tokens = dsl.split(/\s+/);
        let name: string;

        // If only "CREATE FOLDER" is provided.
        if (tokens.length === 2) {
            name = `Folder${this.folderCounter++}`;
        } else {
            // If the third token is "NAME", require additional tokens for the name.
            if (tokens[2].toUpperCase() === "NAME") {
                if (tokens.length < 4) {
                    return Result.err("Folder name not specified after NAME keyword.");
                }
                // Join the rest of the tokens to form the folder name.
                name = tokens.slice(3).join(" ");
            } else {
                // If no "NAME" keyword is present, assume tokens after "CREATE FOLDER" form the name.
                name = tokens.slice(2).join(" ");
            }
        }
        // Return the parsed folder name.
        return Result.ok({ name });
    }

    /**
     * Creates a default plane using the provided origin.
     * The default plane is aligned such that its normal is along the Z-axis,
     * ensuring the Z coordinate provided in the origin is respected.
     * The X-axis of the plane (xvec) is set to the unitX direction.
     */
    private createDefaultPlane(origin: XYZ): Plane {
        // Return a plane with:
        // - origin: as provided by the DSL (retaining its Z value)
        // - normal: XYZ.unitZ to orient the plane perpendicular to the XY plane
        // - xvec: XYZ.unitX to define the plane's X direction.
        return new Plane(origin, XYZ.unitZ, XYZ.unitX);
    }

    /**
     * Resolve all variable references in the DSL command string.
     * This method replaces any instance of $varName with the DSL command text that was stored.
     */
    private resolveVariables(dsl: string): Result<string> {
        try {
            // Use a regex to look for all occurrences of $ followed by a valid variable name.
            const resolvedDsl = dsl.replace(/\$([a-zA-Z][a-zA-Z0-9_]*)/g, (match, variableName) => {
                // Try to retrieve the stored DSL command text for this variable name.
                const storedCommand = this.variableCommands.get(variableName);
                if (!storedCommand) {
                    throw new Error(`Unknown variable: ${variableName}`);
                }
                // Replace the variable reference with the stored DSL command text.
                return storedCommand;
            });
            return Result.ok(resolvedDsl);
        } catch (error) {
            return Result.err(`Error resolving variables: ${error}`);
        }
    }

    // Optional: Clears all stored variables.
    public clearVariables(): void {
        this.variableCommands.clear();
    }

    // Optional: Retrieve the saved DSL command text for a variable.
    public getVariableCommand(name: string): string | undefined {
        return this.variableCommands.get(name);
    }
}
